import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
  find,
  isEqual,
} from 'lodash';
import withRouter from '../../../wrappers/withRouter';
import {
  tablePropsType,
  tablePropsDefault,
} from '../../../types/tablePropsType';
import {
  getSettings,
  removeSetting,
  getFiltersFromUrl,
  getData,
  saveViewCards,
} from '../../../helpers/Table';
import {
  getParam,
} from '../../../helpers/Navigation';
import API from '../../../api';
import VibeTable from '../VibeTable';
import MessageBlockCards from './Cards/MessageBlockCards';
import VibeModal from '../../VibeModal/VibeModal';
import ContentTags from '../CellTypes/ContentTags';
import CellTooltip from '../CellTypes/CellTooltip';
import VibeIcon from '../../VibeIcon/VibeIcon';
import viStore from '../../../icons/viStore';
import viViewCard from '../../../icons/viViewCard';
import viAddCircle from '../../../icons/viAddCircle';
import viCloseCircle from '../../../icons/viCloseCircle';
import * as color from '../../../sass/color.scss';

const tableId = 'table:message:blocks';
const permissionPrefix = 'messagelist';

class TableMessageBlocks extends PureComponent {
  constructor(props) {
    super(props);

    const {
      columns: columnNames,
      defaultSortBy,
      pageSize,
      paginatorProps: {
        urlPaging,
        urlFilters,
      },
    } = props;

    const columnsDef = [{
      name: '.',
      defaultWidth: 40,
    },
    {
      name: '...',
      defaultWidth: 72,
    },
    {
      name: 'Add/Remove',
      defaultWidth: 77,
      icon: viAddCircle,
      locked: true,
    },
    {
      name: 'Name',
      searchAttr: 'name',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
      sortable: true,
      autoFocus: true,
    },
    {
      name: 'Company',
      searchAttr: 'companyName',
      defaultWidth: 187,
      resizable: true,
      searchable: true,
      sortable: true,
    },
    {
      name: 'Integration',
      searchAttr: 'listType',
      defaultWidth: 187,
      resizable: true,
    },
    {
      name: 'Frequency',
      searchAttr: 'frequency',
      defaultWidth: 187,
      resizable: true,
    },
    {
      name: 'Consecutive',
      searchAttr: 'groupSize',
      defaultWidth: 143,
      resizable: true,
    },
    {
      name: 'Start Type',
      searchAttr: 'startType',
      defaultWidth: 140,
      resizable: true,
    },
    {
      name: 'Sequence',
      searchAttr: 'sequence',
      defaultWidth: 187,
      resizable: true,
    },
    {
      name: 'Locations',
      searchAttr: 'locationCount',
      defaultWidth: 110,
      icon: viStore,
      resizable: true,
    },
    {
      name: 'Tags',
      searchAttr: 'tags.name',
      defaultWidth: 275,
      resizable: true,
      searchable: true,
    }];

    const settings = getSettings({
      tableId,
      columnsDef,
      columnNames,
    });

    this.state = {
      columnsDef,
      columnNames,
      rows: [],
      loading: true,
      totalItems: 0,
      active: !urlFilters || (urlFilters && getParam('active') !== 'false'),
      pageNumber: urlPaging
        ? parseInt(getParam('page') || 1, 10)
        : 1,
      pageSize: get(settings, 'pageSize', pageSize),
      sortBy: {
        label: get(settings, 'sortBy.label', defaultSortBy.label),
        attr: get(settings, 'sortBy.attr', defaultSortBy.attr),
        direction: get(settings, 'sortBy.direction', defaultSortBy.direction),
      },
      filters: urlFilters
        ? getFiltersFromUrl({ columns: columnsDef })
        : {},
      confirm: false,
      confirmRow: {},
      confirmAction: '',
      confirmTitle: '',
      confirmText: '',
      confirmApproveColor: '',
      showCards: get(settings, 'cards', false),
    };

    // listen for when sidebar data changes
    document.addEventListener('onSaveMessageBlock', this.onUpdateTableData);
    document.addEventListener('onUpdateTableAssignedMessageBlocks', this.onUpdateTableAssignedData);
  }

  componentDidMount() {
    const {
      showCards,
    } = this.state;

    if (!showCards) {
      this.getData();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      fetch,
      collection,
    } = this.props;

    const {
      collection: prevCollection,
    } = prevProps;

    if (!fetch && !isEqual(collection, prevCollection)) {
      this.onUpdate({
        refresh: true,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('onSaveMessageBlock', this.onUpdateTableData);
    document.removeEventListener('onUpdateTableAssignedMessageBlocks', this.onUpdateTableAssignedData);
  }

  /**
   * When an event asks the table to update the table data
   */
  onUpdateTableData = () => {
    const {
      showCards,
    } = this.state;

    if (!showCards) {
      this.onUpdate({
        refresh: true,
      });
    }
  };

  /**
   * When an event asks the table to update the assigned table
   */
  onUpdateTableAssignedData = () => {
    const {
      assigned,
    } = this.props;

    if (assigned) {
      this.onUpdate({
        refresh: true,
      });
    }
  };

  onSelectMenuItem = (itemName, _rowId) => {
    const {
      rows,
    } = this.state;

    const row = find(rows, { _rowId });

    if (!row) {
      console.error('Row not found matching ID', _rowId);
      return;
    }

    switch (itemName) {
      case 'archive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: 'Archive Message Block',
          confirmText: `Are you sure you want to archive ${row.name}?`,
          confirmApproveColor: 'error',
        });

        break;
      }

      case 'unarchive': {
        this.setState({
          confirm: true,
          confirmAction: itemName,
          confirmRow: row,
          confirmTitle: 'Unarchive Message Block',
          confirmText: `Are you sure you want to unarchive ${row.name}?`,
          confirmApproveColor: 'warning',
        });

        break;
      }

      default:
        break;
    }
  };

  onConfirmModal = async () => {
    const {
      collection,
      onRemove,
    } = this.props;

    const {
      confirmAction,
      confirmRow: {
        _id,
      },
    } = this.state;

    if (!_id) {
      console.error('onConfirmModal no row ID');
      return;
    }

    switch (confirmAction) {
      case 'archive': {
        const response = await API.MessageBlock.deactivate(_id);

        const successText = 'MESSAGELIST.DEACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // if using a local collection, remove from the table
          if (collection.length > 0) {
            const items = collection.filter(item => item._id === _id);

            items.forEach((item) => {
              // remove the item from the table
              onRemove(item);
            });
          }

          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      case 'unarchive': {
        const response = await API.MessageBlock.reactivate({
          _id,
        });

        const successText = 'MESSAGELIST.REACTIVATED';
        const success = get(response, '[0].type') === successText;

        if (success) {
          // tell listening components to update the counts and table data
          this.onUpdate({
            refresh: true,
          });
        }

        break;
      }

      default:
        break;
    }

    this.resetConfirmModal();
  };

  /**
   * When the Bulk Archive Completes
   */
  onBulkArchive = () => {
    // tell listening components to update the counts and table data
    this.onUpdate({
      refresh: true,
    });
  };

  /**
   * Remove a Message Block from the table
   */
  onRemoveMessageBlock = (e, _id) => {
    const {
      onRemove,
    } = this.props;

    const {
      rows,
    } = this.state;

    const row = find(rows, { _id });
    onRemove(row);
  };

  /**
   * Add a Message Block to the table
   */
  onAddMessageBlock = (e, _id) => {
    const {
      onAdd,
    } = this.props;

    const {
      rows,
    } = this.state;

    const row = find(rows, { _id });
    onAdd(row);
  };

  /**
   * Toggle the table view
   */
  onViewTable = () => {
    removeSetting({
      tableId,
      setting: 'cards',
    });

    this.setState({
      showCards: false,
    });

    this.onUpdate({
      refresh: true,
    });
  };

  /**
   * Toggle the card view
   */
  onViewCards = () => {
    saveViewCards({
      tableId,
      cards: true,
    });

    this.setState({
      showCards: true,
    });
  };

  /**
   * Reset to Default Settings
   */
  onReset = () => {
    const {
      defaultSortBy,
      pageSize,
    } = this.props;

    this.onUpdate({
      refresh: true,
      data: {
        sortBy: defaultSortBy,
        pageSize: pageSize || 50,
      },
    });
  };

  onUpdate = ({
    data,
    refresh = false,
  }) => {
    if (refresh) {
      // refresh the table data
      this.setState(data, this.getData);
    } else {
      this.setState(data);
    }
  };

  getData = async (config = {}) => {
    const {
      props,
      state,
    } = this;

    if (!state.loading && !config.export) {
      this.setState({
        loading: true,
      });
    }

    const {
      rows,
      filters,
      totalItems,
    } = await getData({
      props,
      state,
      config,
      customData: {
        // always exclude messages for the table except when the rows are draggable
        excludeMessages: !props.draggable,
      },
    });

    if (rows) {
      this.setState({
        loading: false,
        rows,
        totalItems,
      });

      props.onFetchComplete({
        rows,
        filters,
        totalItems,
      });
    }
  };

  /**
   * Fetch data for the table
   * Only request and return the response from the API or collection
   */
  fetchData = async (config = {}) => {
    const {
      props,
      state,
    } = this;

    const {
      rows,
      // totalItems,
    } = await getData({
      props,
      state,
      config,
    });

    return rows;
  };

  /**
   * Reset the confirm modal data
   */
  resetConfirmModal = () => {
    this.setState({
      confirm: false,
    });
  };

  renderCell = ({
    column,
    row,
  }) => {
    const {
      highlight,
    } = this.props;

    // get the attribute with data for the cell
    const attr = column.valueAttr || column.searchAttr;
    const value = get(row, attr, '');

    switch (column.name) {
      case 'Add/Remove': {
        const assigned = find(highlight, { _id: row._id }) !== undefined;

        return (
          <VibeIcon
            name={row._id}
            icon={assigned
              ? viCloseCircle
              : viAddCircle}
            color={assigned
              ? color.error
              : color.success}
            hoverColor={assigned
              ? color.error75
              : color.success75}
            size={24}
            tooltip={assigned
              ? 'Remove Message Block'
              : 'Add Message Block'}
            tooltipProps={{
              placement: 'right',
            }}
            onClick={assigned
              ? this.onRemoveMessageBlock
              : this.onAddMessageBlock}
          />
        );
      }

      case 'Integration':
      case 'Start Type':
      case 'Sequence':
        return (
          <CellTooltip title={value}>
            <div
              className="cell-content"
              style={{
                textTransform: 'capitalize',
              }}
            >
              {value}
            </div>
          </CellTooltip>
        );

      case 'Frequency': {
        const plural = row.frequency !== 1
          ? 's'
          : '';

        const frequency = row.listType === 'duration'
          ? `${value} minute${plural}`
          : `${value} song${plural}`;

        return (
          <CellTooltip title={frequency}>
            <div className="cell-content">
              {frequency}
            </div>
          </CellTooltip>
        );
      }

      case 'Tags':
        return (
          <ContentTags
            className="cell-content"
            tags={row.tags}
            hideIcon
          />
        );

      default:
        return (
          <CellTooltip title={value}>
            <div className="cell-content">
              {value}
            </div>
          </CellTooltip>
        );
    }
  };

  render() {
    const {
      className,
      fetch,
      fetchProps,
      toolbarProps,
      cardProps,
      draggable,
      toggle,
      disableCardView,
    } = this.props;

    const {
      columnsDef,
      columnNames,
      rows,
      loading,
      totalItems,
      active,
      pageNumber,
      pageSize,
      sortBy,
      filters,
      confirm,
      confirmTitle,
      confirmText,
      confirmApproveColor,
      showCards,
    } = this.state;

    if (showCards) {
      return (
        <MessageBlockCards
          fetch={fetch}
          fetchFilters={fetchProps.filters}
          cardProps={cardProps}
          customButtons={toolbarProps.customButtons}
          draggable={draggable}
          toggle={toggle}
          onViewTable={this.onViewTable}
        />
      );
    }

    // add card view icon
    const newToolbarProps = {
      ...toolbarProps,
      // show the toggle to switch between table/card view
      customHtmlRight: disableCardView
        ? toolbarProps.customHtmlRight
        : (
          <VibeIcon
            className="density-icon"
            icon={viViewCard}
            color={color.whiteSmoke}
            hoverColor={color.secondary}
            size={24}
            tooltip="Card View"
            tooltipProps={{
              placement: 'top',
            }}
            onClick={this.onViewCards}
          />
        ),
    };

    return (
      <div className={classNames('Table', 'TableMessageBlocks', className)}>
        <VibeTable
          {...this.props}
          toolbarProps={newToolbarProps}
          tableId={tableId}
          columnsDef={columnsDef}
          columnNames={columnNames}
          rows={rows}
          loading={loading}
          permissionPrefix={permissionPrefix}
          sortBy={sortBy}
          filters={filters}
          totalItems={totalItems}
          active={active}
          pageNumber={pageNumber}
          pageSize={pageSize}
          renderCell={this.renderCell}
          bulkArchive={API.MessageBlock.deactivateBulk}
          onSelectMenuItem={this.onSelectMenuItem}
          onBulkArchive={this.onBulkArchive}
          onReset={this.onReset}
          onFetch={this.fetchData}
          onUpdate={this.onUpdate}
        />

        <VibeModal
          show={confirm}
          type="confirm"
          confirmProps={{
            color: confirmApproveColor,
          }}
          title={confirmTitle}
          text={confirmText}
          onConfirm={this.onConfirmModal}
          onClose={this.resetConfirmModal}
        />
      </div>
    );
  }
}

TableMessageBlocks.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  disableCardView: PropTypes.bool,
  ...tablePropsType,
};

TableMessageBlocks.defaultProps = {
  disableCardView: false,
  ...tablePropsDefault,
};

export default withRouter(TableMessageBlocks);
